import { useCallback, useState } from 'react';
import assign from 'lodash/assign';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { parseQueryString } from '@zg-rentals/string-utils';

// Actions
import { fetchAutocompleteData, goToAddressItem } from '../../flux/actions/AddressActions';

import { useGetUser } from '../../hooks/queries/user';
import { useGetAddress } from '../../hooks/queries/address';
import { useLocation } from '../../hooks/useLocation';

import validator from '../../utils/validator';
// @ts-ignore
import { augmentError } from '../../utils/errors';

import errorConstants from '../../constants/ErrorConstants';

import AutocompleteAddress from '../../models/AutocompleteAddress';

import type { Dispatch, SetStateAction } from 'react';
import type Address from '../../models/Address';

type ErrorType = {
  nonCriticalError: boolean;
  errorCode: string;
  errorMsg: string;
};

// const logger = getLogger('SearchModule/hooks');

export const useSuggestions = (
  recentSearches: Array<AutocompleteAddress>,
): {
  suggestions: Array<AutocompleteAddress>;
  getSuggestions: (query: string) => void;
} => {
  const initState = isEmpty(recentSearches) ? [] : recentSearches;
  const [suggestions, updateSuggestions] = useState(initState);

  // eslint-disable-next-line
  const getSuggestions = useCallback(
    debounce((query: string) => {
      // address api requires 7 characters
      if (query.length >= 7) {
        fetchAutocompleteData(query)()
          // eslint-disable-next-line
          .then((res: any) => {
            updateSuggestions(res);
            return;
          })
          // eslint-disable-next-line
          .catch((err: any) => {
            // logger.error('Error fetching atocomplete suggestions', err);
          });
      } else {
        updateSuggestions(initState);
      }
    }, 200),
    [updateSuggestions],
  );

  return { suggestions, getSuggestions };
};

export const useEmail = (): {
  email: string;
  updateEmail: (query: string) => void;
  validateEmail: (emailStr: string) => boolean;
  emailError: string;
} => {
  const [email, updateEmail] = useState('');
  const [emailError, updateEmailError] = useState('');

  const { updateUserEmail } = useGetUser();

  const validateEmail = (emailStr: string) => {
    // TODO: check if email is associated with current ZG account
    const validatorObj = validator.email(emailStr);

    if (validatorObj.isValid) {
      updateEmailError('');

      // NOTE: this is ok since this will only every be utilized when the user is not logged in
      updateUserEmail({ email: emailStr });
    } else {
      updateEmailError((validatorObj as { isValid: boolean; message: string }).message);
    }

    return validatorObj.isValid;
  };

  return { email, updateEmail, validateEmail, emailError };
};

export const useGoToAddressItem = () => {
  const location = useLocation();

  const urlQueryObject = parseQueryString(location.search);

  const { refetch: getUser } = useGetUser();
  const { data: address } = useGetAddress();

  const [addressError, updateAddressError] = useState('');
  const [isNoZestModalOpen, openNoZestModal] = useState(false);

  const validateAndGoToAddressItem = async ({
    addressInputValue,
    isPartiallyAuthenticated,
    email,
    setIsLoading,
    unit = '',
    validAddressItem,
    validateEmail,
    addRecentSearch,
    setAddress,
  }: {
    addressInputValue: string;
    isPartiallyAuthenticated: boolean;
    email: string;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    unit?: string;
    validAddressItem: AutocompleteAddress | null;
    validateEmail: (emailStr: string) => boolean;
    addRecentSearch: (
      recentSearch: AutocompleteAddress & {
        isRecent: boolean;
        unit: string;
      },
    ) => void;
    setAddress: (newAddressToSet: Address) => void;
  }) => {
    /**
     * validate address
     * - validAddressItem was selected from the autocomplete dropdown
     * - addressInputValue has not been changed after selecting address item
     */
    if (!validAddressItem || addressInputValue !== validAddressItem.name) {
      const errMsg = 'Invalid street address. Please select an item from the dropdown.';
      updateAddressError(errMsg);
      return Promise.reject(new Error(errMsg));
    }

    // validate email after validating address so that only one error exists at a time
    const hasValidEmail = isPartiallyAuthenticated || validateEmail(email);
    if (!hasValidEmail) {
      const errMsg = `Invalid email address entered: ${email}`;
      return Promise.reject(new Error(errMsg));
    }

    setIsLoading(true);
    updateAddressError('');

    const addressItem = validAddressItem && new AutocompleteAddress(validAddressItem);
    const augmentedAddressItem = assign(addressItem, {
      unit,
      isRecent: true,
    });

    const { data: user } = await getUser();

    if (!user || !address) {
      return;
    }

    return await goToAddressItem(
      augmentedAddressItem,
      user,
      urlQueryObject,
      addRecentSearch,
      setAddress,
      address,
    )().catch((error: ErrorType) => {
      setIsLoading(false);

      if (error.nonCriticalError) {
        updateAddressError(error.errorMsg);
        if (error.errorCode === errorConstants.RENT_ZESTIMATE_NOT_FOUND) {
          /**
           * NOTE: fixed the issue where the no zestimate modal will result in
           * `null address` by passing in the validAddressItem into NoZestimateModal
           */
          openNoZestModal(true);
        }
        return;
      }

      updateAddressError('Sorry. We could not process your request. Please try again.');
      augmentError(error, {
        hook: 'Error in SearchModule#hooks#validateAndGoToAddressItem',
      });

      throw error;
    });
  };

  return { isNoZestModalOpen, openNoZestModal, addressError, updateAddressError, validateAndGoToAddressItem };
};
